// ── ContactForm ──
// The Account field is here rather than implied by wherever the form was opened
// from. It used to be invisible: the parent passed an `accountid` prop, the form
// saved it silently, and if the parent had no account to pass — Email Review
// creating a contact for an address whose domain matched nothing — the contact
// was written with a NULL accountid. getContacts inner-joins Accounts, so that
// contact then vanished from the contact list entirely and had to be re-linked
// by hand. Showing the field means you can see what it will link to, and fix it
// before saving rather than after.
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { Btn, FInput, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { NotesPanel } from "../../../shared/NotesPanel";
import { DuplicateCheck } from "../../sales/components/DuplicateCheck";
import { EmailActivityPanel } from "../../../shared/EmailActivityPanel";
import { portalState } from "../../../shared/portalAccess";

export const ContactForm = ({ initial, accountid, readOnly, hint }) => {
    const { api, role, modal, setModal, closeModal, saving, error, saveRec } = useApp();
    const [f, set] = useFreshForm(initial||{
      accountid, title:"", firstname:"", lastname:"",
      email:"", mobilephone:"", homephone:"", phone:"",
      street:"", city:"", state:"", country:"", zipcode:"",
      invoicedelivery:false, portalaccess:false, monitornews:false
    }, "Contacts");
    const titleOptions = ["","Mr.","Mrs.","Ms.","Dr.","Prof."];
    const ST = ({ title }) => <div style={{ gridColumn:"1/-1", fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>{title}</div>;

    const isExisting = !!(initial && (initial.Id || initial.id));

    // Sales endpoint first (it's scoped and the Sales app is where most contacts
    // get created); portfolio endpoint as the fallback for a servicer or
    // underwriter who has no sales access and would get a 403 from the first.
    const [accounts, setAccounts] = useState([]);
    useEffect(() => {
      let alive = true;
      api.get("getSalesAccounts")
        .catch(() => api.get("getAccounts"))
        .then(d => { if (alive && Array.isArray(d)) setAccounts(d); })
        .catch(() => {});
      return () => { alive = false; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentAccount = f.accountid || accountid || "";
    const accountOptions = useMemo(() => {
      const opts = [{ value: "", label: accounts.length ? "— Select an account —" : "Loading accounts…" }];
      accounts.forEach(a => opts.push({ value: a.id, label: a.companyname || a.id }));
      // Keep a preselected account visible while the list is still loading, and
      // cover the case where it isn't in this user's visible set — otherwise the
      // select would silently render blank and a save would clear the link.
      if (currentAccount && !accounts.some(a => a.id === currentAccount)) {
        opts.push({ value: currentAccount, label: (initial && initial.companyname) || currentAccount });
      }
      return opts;
    }, [accounts, currentAccount, initial]);

    // A contact with no account disappears from the contact list, so block the
    // save rather than let it happen and be discovered later.
    const missingAccount = !readOnly && !currentAccount;

    return <Modal title={readOnly?"View Contact":isExisting?"Edit Contact":"Add Contact"} onClose={closeModal} wide
      onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"contact-edit",rec:initial,accountid,onDone:modal.onDone}); }:null}>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 24px" }}>
          <ST title="Account" />
          <div style={{ gridColumn:"1/3" }}>
            <Field label="Account">
              <FSelect value={currentAccount} onChange={set("accountid")} options={accountOptions} />
            </Field>
          </div>
          <div style={{ gridColumn:"3/-1", alignSelf:"center", paddingTop:10 }}>
            {hint && !isExisting && (
              <div style={{ fontSize:12, color:"#7a5200" }}>{hint}</div>
            )}
            {missingAccount && !hint && (
              <div style={{ fontSize:12, color:"#993C1D" }}>
                Pick an account — a contact without one won't appear in the contact list.
              </div>
            )}
          </div>

          <ST title="Name" />
          <Field label="Title"><FSelect value={f.title||""} onChange={set("title")} options={titleOptions} /></Field>
          <Field label="First Name"><FInput value={f.firstname||""} onChange={set("firstname")} /></Field>
          <Field label="Last Name"><FInput value={f.lastname||""} onChange={set("lastname")} /></Field>
          <div />

          <ST title="Contact Info" />
          {/* Email is unique across Contacts now — surface the collision here
              rather than letting the save come back with a constraint error. */}
          {!readOnly && !isExisting && (
            <div style={{ gridColumn:"1/-1" }}>
              <DuplicateCheck email={f.email} />
            </div>
          )}
          <Field label="Email"><FInput value={f.email||""} onChange={set("email")} /></Field>
          <Field label="Phone"><FInput value={f.phone||""} onChange={set("phone")} /></Field>
          <Field label="Mobile Phone"><FInput value={f.mobilephone||""} onChange={set("mobilephone")} /></Field>
          <Field label="Home Phone"><FInput value={f.homephone||""} onChange={set("homephone")} /></Field>

          <ST title="Address" />
          <Field label="Street"><FInput value={f.street||""} onChange={set("street")} /></Field>
          <Field label="City"><FInput value={f.city||""} onChange={set("city")} /></Field>
          <Field label="State"><FInput value={f.state||""} onChange={set("state")} /></Field>
          <Field label="Country"><FInput value={f.country||""} onChange={set("country")} /></Field>
          <Field label="Zip Code"><FInput value={f.zipcode||""} onChange={set("zipcode")} /></Field>
          <div /><div /><div />

          <ST title="Access & Delivery" />
          <div style={{ gridColumn:"1/-1", display:"flex", gap:28, marginBottom:8 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.invoicedelivery} onChange={e=>set("invoicedelivery")(e.target.checked)} /> Invoice Delivery
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.portalaccess} onChange={e=>set("portalaccess")(e.target.checked)} /> Should have portal access
            </label>
            {/* News Monitor searches this person's name daily for negative news.
                Portfolio managers and admins only; the server keeps the stored
                value for anyone else. Searched only while the account is funded. */}
            {role.canViewMonitoring && (
              <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}
                     title="Search this person's name in the news every day (News Monitor). Funded accounts only.">
                <input type="checkbox" checked={!!f.monitornews} onChange={e=>set("monitornews")(e.target.checked)} /> Monitor news
              </label>
            )}
          </div>

          {/* ── The two facts, side by side ──────────────────────────────────
              The checkbox above is the LEGACY flag. It records an intention —
              somebody decided this person should be able to sign in — and it
              cannot make that true on its own, because the portal login lives
              in Client Portal Login.

              The line below is the actual state, derived from that login. It is
              read-only on purpose: an editable control over a derived value is
              a control that looks like it works and doesn't.

              Only shown on a saved contact. On a new one there is nothing to
              derive from yet, and an empty "no login" would read as a problem
              rather than as a record that does not exist. */}
          {isExisting && (
            <div style={{ gridColumn:"1/-1", marginTop:4, marginBottom:8, fontSize:12.5,
                          display:"flex", alignItems:"baseline", gap:10, lineHeight:1.55 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Portal login:</span>
              {(() => {
                const s = portalState(initial || {});
                return <>
                  {s.bg
                    ? <span style={{ fontSize:11.5, background:s.bg, color:s.fg, padding:"2px 8px",
                                     borderRadius:4, fontWeight:600, whiteSpace:"nowrap" }}>{s.label}</span>
                    : <span style={{ color:"var(--color-text-secondary)" }}>None</span>}
                  <span style={{ color:"var(--color-text-secondary)" }}>
                    {s.note || "Managed in Administration → Client Portal Login."}
                  </span>
                </>;
              })()}
            </div>
          )}
        </div>
      </ReadOnlyForm>
      {(initial?.Id || initial?.id) && (
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>Notes</div>
          <NotesPanel entitytype="Contact" entityid={initial.Id || initial.id} accountid={f.accountid || accountid} />
        </div>
      )}
      {(initial?.Id || initial?.id) && (
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>Email</div>
          <EmailActivityPanel entitytype="Contact" entityid={initial.Id || initial.id} />
        </div>
      )}
      {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{error}</div>}
      <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
        <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
        {!readOnly&&<Btn disabled={saving||missingAccount} onClick={()=>saveRec("saveContact",{...f,accountid:currentAccount},initial?"Edited":"Added","Contacts",(initial?"Updated":"Added")+" contact",modal.onDone)} variant="primary">{saving?"Saving...":"Save"}</Btn>}
      </div>
    </Modal>;
  };
